/**
 * Side preview for the defend / intercept prompts: the attacker's card
 * art striking toward the attacked card's art, joined by a forged
 * metal arrow crowned with crossed swords. Rendered to the RIGHT of
 * the centered prompt stack (CardActionMenu's sideContent slot),
 * vertically centered on it. So the defender sees WHO is attacking
 * WHAT at a glance, with no copy needed.
 *
 * Intercept windows open mid-move (no attack target yet), so they
 * show the moving enemy's card alone, without the arrow.
 */
import { ACCENT_GOLD, CONTENT_BG_DEFAULT, RADIUS } from '../../lib/medievalTheme';
import FoilCanvas from '../FoilCanvas';
import { resolveLocalImageUrl } from '../../utils/localApi';
import { isFoilFinish } from '../../utils/sorcery/foil.js';
import { foilZonesForCard } from '../../utils/sorcery/foilZones.js';
import { DEFAULT_FOIL_PARAMS } from '../../utils/sorcery/foilParams.js';
import { t } from '../../localization/t';

const OPAQUE_BASE = '#0e0c08';
const ART_W = 6.4;  // cqw
const ART_H = 8.9;  // cqw. True card aspect (~0.72)

// Forged-gold material shared by the arrow shaft and head: theme grit
// textures blended over a bronze gradient, same recipe family as
// TEXTURED_PANEL_STYLE so the arrow reads as part of the set.
const FORGED_GOLD = {
  background: `${CONTENT_BG_DEFAULT}, linear-gradient(180deg, #ecc878 0%, #c2944a 48%, #7d5520 100%)`,
  backgroundBlendMode: 'overlay, normal',
};

function Portrait({ info, fallback }) {
  const isSite = !!info?.isSite;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3cqw', width: `${ART_W + 1}cqw` }}>
      <div style={{
        width: `${ART_W}cqw`, height: `${ART_H}cqw`,
        borderRadius: RADIUS.card, overflow: 'hidden',
        backgroundColor: OPAQUE_BASE,
        boxShadow: '0 0 0 1px rgba(212, 168, 67, 0.55), 0 0.35cqw 0.9cqw rgba(0, 0, 0, 0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative',
      }}>
        {info?.imageUrl ? (
          <>
            <img
              src={info.imageUrl}
              alt=""
              draggable={false}
              style={isSite ? {
                // Site art ships portrait; rotate 90° CW to read landscape
                // inside the portrait box (mirrors AttackTargetModal sites).
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: `${ART_H}cqw`,
                height: `${ART_W}cqw`,
                transform: 'translate(-50%, -50%) rotate(90deg)',
                transformOrigin: 'center center',
                objectFit: 'cover',
                pointerEvents: 'none',
              } : { width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
            />
            {isFoilFinish(info?.foiling) ? (
              <FoilCanvas imageUrl={resolveLocalImageUrl(info.imageUrl)} zones={foilZonesForCard({ isSite })} params={DEFAULT_FOIL_PARAMS} scrollSweep={false} rotate90={isSite} />
            ) : null}
          </>
        ) : (
          <div style={{ color: ACCENT_GOLD, fontSize: '0.7cqw', textAlign: 'center', padding: '0.3cqw' }}>
            {info?.name || fallback}
          </div>
        )}
      </div>
      <div style={{
        maxWidth: `${ART_W + 1}cqw`,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        color: ACCENT_GOLD, fontSize: '0.8cqw', fontWeight: 700,
        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
      }}>
        {info?.name || fallback}
      </div>
    </div>
  );
}

// The forged arrow: beveled gold shaft + clipped arrowhead. Depth
// comes from inset bevels on the shaft and a group-level drop shadow
// (clip-path strips box-shadows off the head, so the filter carries
// its relief).
function StrikeArrow() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      filter: 'drop-shadow(0 0.2cqw 0.4cqw rgba(0,0,0,0.7))',
    }}>
      <div style={{
        width: '3.9cqw', height: '0.8cqw',
        borderRadius: 'var(--radius-sm) 0 0 var(--radius-sm)',
        ...FORGED_GOLD,
        boxShadow: [
          'inset 0 0.12cqw 0.1cqw rgba(255, 240, 200, 0.55)',
          'inset 0 -0.14cqw 0.12cqw rgba(40, 22, 4, 0.7)',
          'inset 0.1cqw 0 0.08cqw rgba(255, 240, 200, 0.25)',
          '0 0 0 1px rgba(70, 45, 12, 0.9)',
        ].join(', '),
      }} />
      <div style={{
        width: '1.7cqw', height: '2.1cqw',
        marginLeft: '-0.05cqw',
        clipPath: 'polygon(0 0, 100% 50%, 0 100%)',
        ...FORGED_GOLD,
      }} />
    </div>
  );
}

export default function CombatMatchupPreview({ attacker, target }) {
  if (!attacker && !target) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.9cqw' }}>
      <Portrait info={attacker} fallback={t('Attacker')} />
      {target ? (
        <>
          {/* Arrow sits at the card ART midline, not the row middle.
              the name row under the art would otherwise drag it low. */}
          <div style={{ marginTop: `${ART_H / 2 - 1.05}cqw` }}>
            <StrikeArrow />
          </div>
          <Portrait info={target} fallback={t('Target')} />
        </>
      ) : null}
    </div>
  );
}
